import React from "react";
import {
  X,
  Check,
  User,
  CreditCard,
  Shield,
  Car,
  MapPin,
  Camera,
} from "lucide-react";
import { toast } from "sonner";

const EditKycModal = ({
  editKycModalOpen,
  setEditKycModalOpen,
  editKycDriver,
  setEditKycDriver,
  currentStep,
  setCurrentStep,
  kycFormdata,
  setKycFormData,
  drivers,
  handleDriverSelect,
  handleFileChange,
  handleStepSubmit,
  uploadImageToServer,
  resetForm,
  Loading,
  onSuccess,
  activeTab,
  FetchDriverKycDetails,
}) => {
  const steps = [
    { id: 1, label: "Address", icon: MapPin },
    { id: 2, label: "Profile Picture", icon: Camera },
    { id: 3, label: "Aadhaar Card", icon: CreditCard },
    { id: 4, label: "PAN Card", icon: Shield },
    { id: 5, label: "Driving License", icon: Car },
    { id: 6, label: "Vehicle Details", icon: User },
  ];

  const handleClose = () => {
    setEditKycModalOpen(false);
    setEditKycDriver(null);
    setCurrentStep(1);
    resetForm();
  };

  const handleNextStep = async (stepNumber, stepData) => {
    if (!kycFormdata.driverId && stepNumber === 1) {
      toast.error("Please select a driver");
      return;
    }

    const success = await handleStepSubmit(stepNumber, stepData);
    if (success) {
      if (stepNumber < 6) {
        setCurrentStep(stepNumber + 1);
      } else {
        toast.success("All KYC steps completed successfully!");
        setTimeout(() => {
          handleClose();
          if (activeTab === "Kyc Details") {
            FetchDriverKycDetails();
          }
          if (onSuccess) onSuccess();
        }, 1500);
      }
    }
  };

  if (!editKycModalOpen) return null;

  return (
    <div>
      {editKycModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-md max-w-6xl w-full max-h-[90vh] overflow-y-auto p-6 relative mx-4">
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-white pb-4 border-b">
              <h2 className="text-xl font-semibold">Edit KYC Data</h2>
              <button
                onClick={() => {
                  setEditKycModalOpen(false);
                  setEditKycDriver(null);
                  setCurrentStep(1);
                  resetForm();
                }}
                className="text-gray-600 hover:text-gray-800"
              >
                <X size={20} />
              </button>
            </div>

            <div className="bg-gradient-to-br from-orange-100 to-amber-100 p-8 rounded-2xl relative overflow-hidden">
              {/* Floating bubbles background */}
              <div className="absolute inset-0">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute rounded-full bg-white bg-opacity-30"
                    style={{
                      width: `${Math.random() * 25 + 15}px`,
                      height: `${Math.random() * 25 + 15}px`,
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      animationDelay: `${Math.random() * 3}s`,
                      animationDuration: `${4 + Math.random() * 3}s`,
                    }}
                  />
                ))}
              </div>

              {/* Additional floating elements */}
              <div className="absolute inset-0">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={`extra-${i}`}
                    className="absolute rounded-full bg-orange-200 bg-opacity-20"
                    style={{
                      width: `${Math.random() * 30 + 10}px`,
                      height: `${Math.random() * 30 + 10}px`,
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      animationDelay: `${Math.random() * 2}s`,
                      animationDuration: `${3 + Math.random() * 2}s`,
                    }}
                  />
                ))}
              </div>

              <div className="relative z-10">
                <div className="flex items-center justify-center space-x-8">
                  {steps.map((step, index) => {
                    const StepIcon = step.icon;
                    const isCompleted = currentStep > step.id;
                    const isCurrent = currentStep === step.id;

                    return (
                      <div key={step.id} className="flex flex-col items-center">
                        <div
                          className={`relative w-16 h-16 rounded-full transition-all duration-500 flex items-center justify-center ${
                            isCompleted
                              ? "bg-green-400 text-white shadow-lg transform scale-110"
                              : isCurrent
                              ? "bg-orange-400 text-white shadow-xl transform scale-125"
                              : "bg-white text-gray-400 shadow-md"
                          }`}
                        >
                          {/* Ripple effect for current step */}
                          {isCurrent && (
                            <>
                              <div className="absolute inset-0 rounded-full bg-orange-400 animate-ping opacity-30"></div>
                              <div className="absolute inset-0 rounded-full bg-orange-300 animate-ping opacity-20"></div>
                            </>
                          )}

                          {/* Glow effect for completed steps */}
                          {isCompleted && (
                            <div className="absolute inset-0 rounded-full bg-green-300 animate-pulse opacity-40"></div>
                          )}

                          <div className="relative z-10">
                            {isCompleted ? (
                              <Check size={24} />
                            ) : (
                              <StepIcon size={24} />
                            )}
                          </div>
                        </div>

                        <span
                          className={`mt-4 text-sm font-medium text-center ${
                            isCompleted
                              ? "text-green-600"
                              : isCurrent
                              ? "text-orange-600"
                              : "text-gray-500"
                          }`}
                        >
                          {step.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="max-w-2xl mx-auto">
              {currentStep === 1 && (
                <div className="flex justify-center">
                  <div className="w-full max-w-5xl bg-white rounded-2xl shadow-sm mt-5 p-8 border">
                    <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
                      Driver Address Information
                    </h3>
                    {!editKycDriver && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Driver <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={kycFormdata.driver_code}
                          onChange={handleDriverSelect}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg 
                                focus:ring-2 focus:ring-orange-500 focus:border-orange-500 
                                shadow-sm mb-4"
                          required
                        >
                          <option value="">Choose Driver</option>
                          {drivers?.map((driver) => (
                            <option
                              key={driver.driverId}
                              value={driver.driverId}
                            >
                              {driver.fullName}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          City <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={kycFormdata.city}
                          onChange={(e) =>
                            setKycFormData((prev) => ({
                              ...prev,
                              city: e.target.value,
                            }))
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg 
                                focus:ring-2 focus:ring-orange-500 focus:border-orange-500 
                                shadow-sm"
                          placeholder="Enter City"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Area <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={kycFormdata.area}
                          onChange={(e) =>
                            setKycFormData((prev) => ({
                              ...prev,
                              area: e.target.value,
                            }))
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg 
                                  focus:ring-2 focus:ring-orange-500 focus:border-orange-500 
                                  shadow-sm"
                          placeholder="Enter Area"
                          required
                        />
                      </div>
                    </div>

                    <div className="flex justify-end mt-10">
                      <button
                        type="button"
                        onClick={async () => {
                          if (
                            !kycFormdata.city ||
                            !kycFormdata.area ||
                            !kycFormdata.driver_code
                          ) {
                            toast.error("Please fill in all required fields");
                            return;
                          }

                          const step1Data = {
                            step1: {
                              city: kycFormdata.city,
                              area: kycFormdata.area,
                            },
                          };

                          const success = await handleStepSubmit(1, step1Data);
                          if (success) {
                            setCurrentStep(2);
                          }
                        }}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 
                                  rounded-lg shadow-md transition duration-200 font-medium"
                        disabled={Loading}
                      >
                        {Loading ? "Saving..." : "Next"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="flex justify-center">
                  <div className="w-full max-w-3xl bg-white rounded-2xl shadow-sm p-8 mt-5 border">
                    <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
                      Upload Profile Picture
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50 w-full">
                        <label
                          htmlFor="profile_photo"
                          className="block text-sm font-medium text-gray-700 mb-3"
                        >
                          Profile Photo <span className="text-red-500">*</span>
                        </label>

                        {kycFormdata.profile_photo ? (
                          <div className="space-y-3 text-center">
                            <img
                              src={
                                typeof kycFormdata.profile_photo === "string"
                                  ? `${
                                      kycFormdata.profile_photo
                                    }?t=${Date.now()}`
                                  : URL.createObjectURL(
                                      kycFormdata.profile_photo
                                    )
                              }
                              alt="Preview"
                              className="w-32 h-32 object-cover rounded-full mx-auto shadow-md border"
                            />
                            <div className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg shadow-sm">
                              {typeof kycFormdata.profile_photo === "string"
                                ? "Current Profile Photo"
                                : kycFormdata.profile_photo.name}
                            </div>
                            <div className="flex gap-2 justify-center">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  handleFileChange(
                                    "profile_photo",
                                    e.target.files[0]
                                  )
                                }
                                className="hidden"
                                id="change_profile_photo"
                              />
                              <label
                                htmlFor="change_profile_photo"
                                className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 
      rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Change
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  handleFileChange("profile_photo", null)
                                }
                                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 
      rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center space-y-3">
                            <p className="text-gray-400 text-sm">
                              No file selected
                            </p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleFileChange(
                                  "profile_photo",
                                  e.target.files[0]
                                )
                              }
                              className="hidden"
                              id="profile_photo"
                            />
                            <label
                              htmlFor="profile_photo"
                              className="cursor-pointer bg-white text-orange-600 border 
                                    border-orange-600 px-4 py-2 rounded-lg 
                                    hover:bg-orange-50 text-sm shadow-sm"
                            >
                              Choose File
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end mt-10 gap-4">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 
      rounded-lg shadow-md transition duration-200 font-medium"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!kycFormdata.profile_photo) {
                            toast.error("Please upload profile photo");
                            return;
                          }

                          try {
                            let imageUrl = kycFormdata.profile_photo;

                            if (kycFormdata.profile_photo instanceof File) {
                              imageUrl = await uploadImageToServer(
                                kycFormdata.profile_photo,
                                kycFormdata.driver_code,
                                "profile_photo"
                              );
                              setKycFormData((prev) => ({
                                ...prev,
                                profile_photo: imageUrl,
                              }));
                            }

                            const step2Data = {
                              step2: {
                                profilePicture: imageUrl,
                              },
                            };

                            const success = await handleStepSubmit(
                              2,
                              step2Data
                            );
                            if (success) {
                              setCurrentStep(3);
                            }
                          } catch (error) {
                            console.error("Profile upload error:", error);
                            toast.error("Failed to upload profile picture");
                          }
                        }}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 
                              rounded-lg shadow-md transition duration-200 font-medium"
                        disabled={Loading}
                      >
                        {Loading ? "Saving..." : "Next"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="flex justify-center mt-5">
                  <div className="w-full max-w-6xl bg-white rounded-2xl shadow-sm p-10 border">
                    <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
                      Aadhaar Information
                    </h3>

                    <div className="mb-8">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Aadhaar Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={kycFormdata.aadhaar_no}
                        onChange={(e) =>
                          setKycFormData((prev) => ({
                            ...prev,
                            aadhaar_no: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg 
            focus:ring-2 focus:ring-orange-500 focus:border-orange-500 
            shadow-sm"
                        placeholder="Enter Aadhaar number"
                        maxLength="12"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="flex flex-col items-center border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50">
                        <label
                          htmlFor="aadhaar_front"
                          className="block text-sm font-medium text-gray-700 mb-3"
                        >
                          Aadhaar Front <span className="text-red-500">*</span>
                        </label>

                        {kycFormdata.aadhaar_front ? (
                          <div className="space-y-3 text-center w-full">
                            <img
                              src={
                                typeof kycFormdata.aadhaar_front === "string"
                                  ? `${
                                      kycFormdata.aadhaar_front
                                    }?t=${Date.now()}`
                                  : URL.createObjectURL(
                                      kycFormdata.aadhaar_front
                                    )
                              }
                              alt="Aadhaar Front"
                              className="w-full max-h-64 object-contain rounded-md mx-auto shadow-md border"
                            />
                            <div className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg shadow-sm">
                              {typeof kycFormdata.aadhaar_front === "string"
                                ? "Current Aadhaar Front"
                                : kycFormdata.aadhaar_front.name}
                            </div>
                            <div className="flex gap-2 justify-center">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  handleFileChange(
                                    "aadhaar_front",
                                    e.target.files[0]
                                  )
                                }
                                className="hidden"
                                id="change_aadhaar_front"
                              />
                              <label
                                htmlFor="change_aadhaar_front"
                                className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 
                    rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Change
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  handleFileChange("aadhaar_front", null)
                                }
                                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 
                    rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center space-y-3">
                            <p className="text-gray-400 text-sm">
                              No file selected
                            </p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleFileChange(
                                  "aadhaar_front",
                                  e.target.files[0]
                                )
                              }
                              className="hidden"
                              id="aadhaar_front"
                            />
                            <label
                              htmlFor="aadhaar_front"
                              className="cursor-pointer bg-white text-orange-600 border 
                  border-orange-600 px-4 py-2 rounded-lg 
                  hover:bg-orange-50 text-sm shadow-sm"
                            >
                              Choose File
                            </label>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col items-center border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50">
                        <label
                          htmlFor="aadhaar_back"
                          className="block text-sm font-medium text-gray-700 mb-3"
                        >
                          Aadhaar Back <span className="text-red-500">*</span>
                        </label>

                        {kycFormdata.aadhaar_back ? (
                          <div className="space-y-3 text-center w-full">
                            <img
                              src={
                                typeof kycFormdata.aadhaar_back === "string"
                                  ? `${
                                      kycFormdata.aadhaar_back
                                    }?t=${Date.now()}`
                                  : URL.createObjectURL(
                                      kycFormdata.aadhaar_back
                                    )
                              }
                              alt="Aadhaar Back"
                              className="w-full max-h-64 object-contain rounded-md mx-auto shadow-md border"
                            />
                            <div className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg shadow-sm">
                              {typeof kycFormdata.aadhaar_back === "string"
                                ? "Current Aadhaar Back"
                                : kycFormdata.aadhaar_back.name}
                            </div>
                            <div className="flex gap-2 justify-center">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  handleFileChange(
                                    "aadhaar_back",
                                    e.target.files[0]
                                  )
                                }
                                className="hidden"
                                id="change_aadhaar_back"
                              />
                              <label
                                htmlFor="change_aadhaar_back"
                                className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 
                    rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Change
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  handleFileChange("aadhaar_back", null)
                                }
                                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 
                    rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center space-y-3">
                            <p className="text-gray-400 text-sm">
                              No file selected
                            </p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleFileChange(
                                  "aadhaar_back",
                                  e.target.files[0]
                                )
                              }
                              className="hidden"
                              id="aadhaar_back"
                            />
                            <label
                              htmlFor="aadhaar_back"
                              className="cursor-pointer bg-white text-orange-600 border 
                  border-orange-600 px-4 py-2 rounded-lg 
                  hover:bg-orange-50 text-sm shadow-sm"
                            >
                              Choose File
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end mt-10 gap-4">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 
            rounded-lg shadow-md transition duration-200 font-medium"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (
                            !kycFormdata.aadhaar_no ||
                            !kycFormdata.aadhaar_front ||
                            !kycFormdata.aadhaar_back
                          ) {
                            toast.error("Please fill in all required fields");
                            return;
                          }

                          try {
                            let frontImageUrl = kycFormdata.aadhaar_front;
                            let backImageUrl = kycFormdata.aadhaar_back;

                            if (typeof kycFormdata.aadhaar_front !== "string") {
                              frontImageUrl = await uploadImageToServer(
                                kycFormdata.aadhaar_front,
                                kycFormdata.driver_code,
                                "aadhaar_front"
                              );
                            }

                            if (typeof kycFormdata.aadhaar_back !== "string") {
                              backImageUrl = await uploadImageToServer(
                                kycFormdata.aadhaar_back,
                                kycFormdata.driver_code,
                                "aadhaar_back"
                              );
                            }

                            const step3Data = {
                              step3: {
                                aadharNumber: kycFormdata.aadhaar_no,
                                aadharFrontPhoto: frontImageUrl,
                                aadharBackPhoto: backImageUrl,
                              },
                            };

                            const success = await handleStepSubmit(
                              3,
                              step3Data
                            );
                            if (success) {
                              setCurrentStep(4);
                            }
                          } catch (error) {
                            toast.error("Failed to upload Aadhaar images");
                            console.error("Aadhaar upload error:", error);
                          }
                        }}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 
            rounded-lg shadow-md transition duration-200 font-medium"
                        disabled={Loading}
                      >
                        {Loading ? "Saving..." : "Next"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 4 && (
                <div className="flex justify-center mt-5">
                  <div className="w-full max-w-4xl bg-white rounded-2xl shadow-sm p-8 border">
                    <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
                      PAN Card Information
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          PAN Card Number{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={kycFormdata.pan_no}
                          onChange={(e) =>
                            setKycFormData((prev) => ({
                              ...prev,
                              pan_no: e.target.value,
                            }))
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg 
                        focus:ring-2 focus:ring-orange-500 focus:border-orange-500 
                        shadow-sm"
                          placeholder="Enter PAN card number"
                          maxLength="10"
                          required
                        />
                      </div>

                      <div className="flex flex-col items-center border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
                        <label
                          htmlFor="pan_front"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          PAN Card Front Image{" "}
                          <span className="text-red-500">*</span>
                        </label>

                        {kycFormdata.pan_front ? (
                          <div className="space-y-3 text-center">
                            <img
                              src={
                                typeof kycFormdata.pan_front === "string"
                                  ? kycFormdata.pan_front
                                  : URL.createObjectURL(kycFormdata.pan_front)
                              }
                              alt="PAN Front"
                              className="w-32 h-25 object-cover rounded-md mx-auto shadow-md border"
                            />
                            <div className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg shadow-sm">
                              {typeof kycFormdata.pan_front === "string"
                                ? "Current PAN Card"
                                : kycFormdata.pan_front.name}
                            </div>
                            <div className="flex gap-2 justify-center">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  handleFileChange(
                                    "pan_front",
                                    e.target.files[0]
                                  )
                                }
                                className="hidden"
                                id="change_pan_front"
                              />
                              <label
                                htmlFor="change_pan_front"
                                className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 
                            rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Change
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  handleFileChange("pan_front", null)
                                }
                                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 
                            rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center space-y-3">
                            <p className="text-gray-400 text-sm">
                              No file selected
                            </p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleFileChange("pan_front", e.target.files[0])
                              }
                              className="hidden"
                              id="pan_front"
                            />
                            <label
                              htmlFor="pan_front"
                              className="cursor-pointer bg-white text-orange-600 border 
                          border-orange-600 px-4 py-2 rounded-lg 
                          hover:bg-orange-50 text-sm shadow-sm"
                            >
                              Choose File
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end mt-10 gap-4">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 
      rounded-lg shadow-md transition duration-200 font-medium"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!kycFormdata.pan_no || !kycFormdata.pan_front) {
                            toast.error("Please fill in all required fields");
                            return;
                          }

                          try {
                            let panImageUrl = kycFormdata.pan_front;

                            if (typeof kycFormdata.pan_front !== "string") {
                              panImageUrl = await uploadImageToServer(
                                kycFormdata.pan_front,
                                kycFormdata.driver_code,
                                "pan_front"
                              );
                            }

                            const step4Data = {
                              step4: {
                                panCard: kycFormdata.pan_no,
                                panCardFrontPhoto: panImageUrl,
                              },
                            };

                            const success = await handleStepSubmit(
                              4,
                              step4Data
                            );
                            if (success) {
                              setCurrentStep(5);
                            }
                          } catch (error) {
                            toast.error("Failed to upload PAN card image");
                            console.error("PAN upload error:", error);
                          }
                        }}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-2 
                      rounded-lg shadow-md transition duration-200 font-medium"
                        disabled={Loading}
                      >
                        {Loading ? "Saving..." : "Next"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 5 && (
                <div className="flex justify-center mt-5">
                  <div className="w-full max-w-4xl bg-white rounded-2xl shadow-sm p-8 border">
                    <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
                      Driving Licence Information
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Driving Licence Number{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={kycFormdata.driving_licence}
                          onChange={(e) =>
                            setKycFormData((prev) => ({
                              ...prev,
                              driving_licence: e.target.value,
                            }))
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg 
                        focus:ring-2 focus:ring-orange-500 focus:border-orange-500 
                        shadow-sm"
                          placeholder="Enter Driving Licence number"
                          maxLength="16"
                          required
                        />
                      </div>

                      <div className="flex flex-col items-center border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
                        <label
                          htmlFor="driving_licence_img"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Driving Licence Image{" "}
                          <span className="text-red-500">*</span>
                        </label>

                        {kycFormdata.driving_licence_img ? (
                          <div className="space-y-3 text-center">
                            <img
                              src={
                                typeof kycFormdata.driving_licence_img ===
                                "string"
                                  ? kycFormdata.driving_licence_img
                                  : URL.createObjectURL(
                                      kycFormdata.driving_licence_img
                                    )
                              }
                              alt="Driving Licence"
                              className="w-30 h-28 object-cover rounded-md mx-auto shadow-md border"
                            />
                            <div className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg shadow-sm">
                              {typeof kycFormdata.driving_licence_img ===
                              "string"
                                ? "Current Driving License"
                                : kycFormdata.driving_licence_img.name}
                            </div>
                            <div className="flex gap-2 justify-center">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  handleFileChange(
                                    "driving_licence_img",
                                    e.target.files[0]
                                  )
                                }
                                className="hidden"
                                id="change_driving_licence_img"
                              />
                              <label
                                htmlFor="change_driving_licence_img"
                                className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 
                            rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Change
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  handleFileChange("driving_licence_img", null)
                                }
                                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 
                            rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center space-y-3">
                            <p className="text-gray-400 text-sm">
                              No file selected
                            </p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleFileChange(
                                  "driving_licence_img",
                                  e.target.files[0]
                                )
                              }
                              className="hidden"
                              id="driving_licence_img"
                            />
                            <label
                              htmlFor="driving_licence_img"
                              className="cursor-pointer bg-white text-orange-600 border 
                          border-orange-600 px-4 py-2 rounded-lg 
                          hover:bg-orange-50 text-sm shadow-sm"
                            >
                              Choose File
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end mt-10 gap-4">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 
      rounded-lg shadow-md transition duration-200 font-medium"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (
                            !kycFormdata.driving_licence ||
                            !kycFormdata.driving_licence_img
                          ) {
                            toast.error("Please fill in all required fields");
                            return;
                          }

                          try {
                            let dlImageUrl = kycFormdata.driving_licence_img;
                            if (
                              typeof kycFormdata.driving_licence_img !==
                              "string"
                            ) {
                              dlImageUrl = await uploadImageToServer(
                                kycFormdata.driving_licence_img,
                                kycFormdata.driver_code,
                                "driving_licence_img"
                              );
                            }

                            const step5Data = {
                              step5: {
                                drivingLicenseNumber:
                                  kycFormdata.driving_licence,
                                drivingLicenseFrontPhoto: dlImageUrl,
                              },
                            };

                            const success = await handleStepSubmit(
                              5,
                              step5Data
                            );
                            if (success) {
                              setCurrentStep(6);
                            }
                          } catch (error) {
                            toast.error(
                              "Failed to upload driving licence image"
                            );
                            console.error("DL upload error:", error);
                          }
                        }}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-2 
                      rounded-lg shadow-md transition duration-200 font-medium"
                        disabled={Loading}
                      >
                        {Loading ? "Saving..." : "Next"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 6 && (
                <div className="mt-5">
                  <div className="bg-white shadow-sm rounded-2xl p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800 text-center mb-6">
                      Vehicle Information
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Transport Type ID{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={kycFormdata.transport_type_id}
                            onChange={(e) =>
                              setKycFormData((prev) => ({
                                ...prev,
                                transport_type_id: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                          focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            placeholder="Enter Transport Type ID"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Transport Description{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={kycFormdata.transport_description}
                            onChange={(e) =>
                              setKycFormData((prev) => ({
                                ...prev,
                                transport_description: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                              focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            placeholder="Enter Transport Description"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            License Plate Number{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={kycFormdata.licence_plate}
                            onChange={(e) =>
                              setKycFormData((prev) => ({
                                ...prev,
                                licence_plate: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                                focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            placeholder="Enter License Plate Number"
                            required
                          />
                        </div>
                      </div>

                      <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-6 bg-gray-50">
                        <label
                          htmlFor="vehicle_name_plate_img"
                          className="block text-sm font-medium text-gray-700 mb-3"
                        >
                          License Plate Image{" "}
                          <span className="text-red-500">*</span>
                        </label>

                        {kycFormdata.vehicle_name_plate_img ? (
                          <div className="space-y-3 text-center">
                            <img
                              src={
                                typeof kycFormdata.vehicle_name_plate_img ===
                                "string"
                                  ? kycFormdata.vehicle_name_plate_img
                                  : URL.createObjectURL(
                                      kycFormdata.vehicle_name_plate_img
                                    )
                              }
                              alt="License Plate Preview"
                              className="w-38 h-28 object-cover rounded-lg border shadow"
                            />

                            <div className="text-sm text-green-600 bg-green-50 px-3 py-1 rounded">
                              {typeof kycFormdata.vehicle_name_plate_img ===
                              "string"
                                ? "Current License Plate Image"
                                : kycFormdata.vehicle_name_plate_img.name}
                            </div>

                            <div className="flex gap-2 justify-center">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  handleFileChange(
                                    "vehicle_name_plate_img",
                                    e.target.files[0]
                                  )
                                }
                                className="hidden"
                                id="change_vehicle_name_plate_img"
                              />
                              <label
                                htmlFor="change_vehicle_name_plate_img"
                                className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 
                                    rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Change
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  handleFileChange(
                                    "vehicle_name_plate_img",
                                    null
                                  )
                                }
                                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 
                                    rounded-lg shadow-md transition duration-200 font-medium text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3 text-center">
                            <div className="text-gray-400 text-sm mb-3">
                              No file selected
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleFileChange(
                                  "vehicle_name_plate_img",
                                  e.target.files[0]
                                )
                              }
                              className="hidden"
                              id="vehicle_name_plate_img"
                            />
                            <label
                              htmlFor="vehicle_name_plate_img"
                              className="cursor-pointer bg-orange-500 hover:bg-orange-600 text-white
                                        px-5 py-2 rounded-lg shadow-md transition text-sm font-medium"
                            >
                              Choose File
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end mt-6 gap-4">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 
      rounded-lg shadow-md transition duration-200 font-medium"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (
                            !kycFormdata.transport_type_id ||
                            !kycFormdata.transport_description ||
                            !kycFormdata.licence_plate ||
                            !kycFormdata.vehicle_name_plate_img
                          ) {
                            toast.error("Please fill in all required fields");
                            return;
                          }

                          try {
                            let plateImageUrl =
                              kycFormdata.vehicle_name_plate_img;
                            if (
                              typeof kycFormdata.vehicle_name_plate_img !==
                              "string"
                            ) {
                              plateImageUrl = await uploadImageToServer(
                                kycFormdata.vehicle_name_plate_img,
                                kycFormdata.driver_code,
                                "vehicle_name_plate_img"
                              );
                            }

                            const step6Data = {
                              step6: {
                                transportTypeID: kycFormdata.transport_type_id,
                                transportDescription:
                                  kycFormdata.transport_description,
                                licensePlate: kycFormdata.licence_plate,
                                licensePlatePhoto: plateImageUrl,
                              },
                            };

                            const success = await handleStepSubmit(
                              6,
                              step6Data
                            );
                            if (success) {
                              toast.success(
                                "All KYC steps completed successfully!"
                              );

                              setTimeout(() => {
                                setEditKycModalOpen(false);
                                setEditKycDriver(null);
                                setCurrentStep(1);
                                resetForm();

                                if (activeTab === "Add Kyc Data") {
                                  FetchDriverKycDetails();
                                }
                              }, 1500);
                            }
                          } catch (error) {
                            toast.error("Failed to upload vehicle plate image");
                            console.error("Vehicle plate upload error:", error);
                          }
                        }}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-2 
                              rounded-lg shadow-md transition duration-200 font-medium"
                        disabled={Loading}
                      >
                        {Loading ? "Submitting..." : "Submit"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditKycModal;
